/**
 * The four answers that are not surfaces: refused, reason required, consent
 * refused, and declared but not built.
 *
 * Each one says what happened in words rather than showing an empty page. A
 * blank table is the single most expensive failure mode in a permissioned
 * platform, because it is indistinguishable from a true empty result and
 * everybody's first assumption is that the data is missing.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import type { Dict, SectionMeta } from '../api/types'
import { useSession } from '../app/session'
import { appPath } from '../app/paths'
import { Badge, Note } from '../components/bits'
import { Panel } from '../components/Panel'
import { PageHead } from '../components/PageHead'
import { ExplainButton } from '../components/Explainer'

export function Denied({ detail }: { detail?: string }) {
  const { boot } = useSession()
  return (
    <>
      <PageHead title="Not permitted" />
      <Note kind="bad">
        <b>Not permitted.</b> {detail || 'Your role does not hold this surface.'}
      </Note>
      <p className="muted">
        This is an answer rather than a fault. The sentence above names the role
        and what it holds, because a refusal that says only that access is denied
        leaves the person to guess whether the data is missing or their access
        is.
      </p>
      <Link className="btn primary" to={appPath(boot.landing)}>Back to your dashboard</Link>
    </>
  )
}

export function ConsentDenied({ data }: { data: Dict }) {
  const detail = (data.detail || data.reason) as string | undefined
  return (
    <>
      <PageHead title="The consent gateway refused this" />
      <Note kind="bad">
        <b>No consent covers this disclosure.</b> {detail || ''}
      </Note>
      <p className="muted">
        The refusal was recorded. Every access to customer data is logged whether
        it was allowed or denied, which is what makes the disclosure register
        complete rather than a list of successes.
      </p>
    </>
  )
}

/**
 * A surface the role holds, where a reason is recorded before it opens.
 *
 * The reason is posted to the same endpoint the server rendered form posts to,
 * so the reason reaches the audit trail by the same path and lands in the same
 * column. A front end that stored the reason itself and sent it as a header
 * would be a second mechanism, and the first one to be forgotten.
 */
export function ReasonGate({ data }: { data: Dict }) {
  const navigate = useNavigate()
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const target = (data.target as string) || ''
  const sectionKey = (data.section_key as string) || ''

  const submit = async () => {
    setBusy(true)
    setError(null)
    try {
      const result = await api.act('/app/reason', { target, reason })
      navigate(appPath(result.redirect))
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PageHead title="State your reason" />
      <Panel title="This surface records why it was opened"
             explain={sectionKey || 'audit.record_trail'}>
        <p>
          Your role holds <code>{sectionKey}</code>, and the grant carries a
          reason requirement. What you type is written to the audit trail
          alongside your identity, your role and the time, and it is read by
          whoever reviews access to this surface afterwards.
        </p>
        <div className="field wide">
          <label htmlFor="reason">Reason</label>
          <input id="reason" value={reason} minLength={10} autoFocus
                 placeholder="Why this record is being opened"
                 onChange={(e) => setReason(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter' && reason.length >= 10) void submit() }} />
        </div>
        {error && <Note kind="bad">{error}</Note>}
        <button className="btn primary" type="button" disabled={busy || reason.trim().length < 10}
                onClick={() => void submit()}>
          {busy ? 'Opening…' : 'Record the reason and open'}
        </button>
        <p className="xs muted">
          A reason shorter than ten characters is refused by the schema rather
          than by this screen, so a one-letter entry cannot be made to pass by
          calling the endpoint directly.
        </p>
      </Panel>
    </>
  )
}

/**
 * A declared surface that is not built.
 *
 * It renders what the surface will do, what it waits on and how it would be
 * built, because the alternative, an empty table, says the platform is broken
 * where the truth is that this part is scheduled. Permission is still checked
 * on the server before this is shown: a role that will never hold the section
 * is refused rather than shown a roadmap.
 */
export function Placeholder({ data }: { data: Dict }) {
  const { lbl, nm } = useSession()
  const section = data.section as SectionMeta & Dict
  const explainer = data.explainer as Dict | null
  const siblings = (data.siblings || []) as Dict[]
  const depNames = (data.dep_names || {}) as Record<string, string>
  const grantCount = data.grant_count as number
  const depends = (section.depends_on || []) as string[]

  return (
    <>
      <PageHead section={section} />
      <div className="note warn ph-banner">
        <b>Not built. Scheduled for {section.release_phase}.</b> This section is
        declared in the catalogue with its permissions, its explainer and its
        place in the navigation, and it holds no data. {grantCount} role
        {grantCount === 1 ? '' : 's'} already carry a grant on it, so the
        permission model does not change when it is built.
      </div>

      <Panel title="What it will do" explain="inherit">
        <p>{section.planned_summary as string}</p>
        {Boolean(explainer?.purpose_en) && (
          <>
            <h4>Purpose, as declared</h4>
            <p>{explainer!.purpose_en as string}</p>
          </>
        )}
        {Boolean(explainer?.logic_en) && (
          <>
            <h4>How it would work</h4>
            <p>{explainer!.logic_en as string}</p>
          </>
        )}
        <div className="scope-note">
          <Badge tone="muted">{section.section_type}</Badge>
          {section.joget_element && <Badge tone="info">Joget: {section.joget_element}</Badge>}
          <Badge tone="warn">{section.release_phase}</Badge>
          {(section.data_classes || []).map((c: string) => (
            <Badge tone="muted" key={c}>{c}</Badge>
          ))}
        </div>
      </Panel>

      {depends.length > 0 && (
        <Panel title="What it waits on" explain="inherit">
          <ul className="plain">
            {depends.map((key) => (
              <li key={key}>
                <code>{key}</code>
                {depNames[key] ? ` · ${depNames[key]}` : ''}
              </li>
            ))}
          </ul>
          <p className="xs muted">
            Each dependency is a section that exists. A placeholder naming
            something that is not in the catalogue is a defect the verification
            suite fails on, because a plan that points at nothing cannot be
            scheduled.
          </p>
        </Panel>
      )}

      {siblings.length > 0 && (
        <Panel title="Elsewhere in this module" explain="inherit" tight>
          <div className="tbl-wrap">
            <table className="dl">
              <thead><tr><th>Surface</th><th>State</th><th /></tr></thead>
              <tbody>
                {siblings.map((s) => (
                  <tr key={s.section_key as string}>
                    <td data-label="Surface">{nm(s)}</td>
                    <td data-label="State">
                      <Badge tone={s.build_state === 'built' ? 'good' : 'warn'}>
                        {s.build_state === 'built' ? 'built'
                          : `${lbl(s.build_state)} · ${s.release_phase}`}
                      </Badge>
                    </td>
                    <td className="acts">
                      {Boolean(s.route) && (
                        <Link className="btn sm" to={appPath(s.route as string)}>Open</Link>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      <div className="card">
        <header className="card-head">
          Why this is a page and not a hidden menu item
          <ExplainButton sectionKey={section.section_key} />
        </header>
        <div className="card-body">
          <p className="muted">
            Hiding what is not built makes a demonstration look complete and a
            scope conversation impossible. Declaring it puts the breadth of the
            ecosystem in front of the people who have to fund and sequence it,
            and nobody opens one of these expecting working software.
          </p>
        </div>
      </div>
    </>
  )
}
